// Tenants › Signups helpers: labels, and the lookup links on a profile.
//
// The lookup links only open a search in a new tab. Nothing here, or on the
// server, reads LinkedIn or Facebook: their terms forbid scraping, so a person
// looks and decides.

import type { BadgeTone } from '@/components/ui'
import type { OutreachEmailStatus, OutreachStatus, SignupLifecycle, SignupStateKey } from '@/api/growthSignups'

export const OUTREACH_LABELS: Record<OutreachStatus, string> = { not_contacted: 'Not contacted', emailed: 'Emailed', replied: 'Replied', bounced: 'Bounced' }
export const OUTREACH_TONE: Record<OutreachStatus, BadgeTone> = { not_contacted: 'outline', emailed: 'accent', replied: 'good', bounced: 'bad' }

export const EMAIL_STATUS_LABELS: Record<OutreachEmailStatus, string> = { scheduled: 'Scheduled', sending: 'Sending', sent: 'Sent', dry_run: 'Dry run', failed: 'Failed', removed: 'Removed' }
export const EMAIL_STATUS_TONE: Record<OutreachEmailStatus, BadgeTone> = { scheduled: 'accent', sending: 'neutral', sent: 'good', dry_run: 'warn', failed: 'bad', removed: 'outline' }

export interface LookupLink {
  label: string
  href: string
}

/**
 * Search links for a person: LinkedIn people search (name and league), Google and
 * Facebook. Empty when there's no name to search for.
 */
export function lookupLinks(name: string | null | undefined, league: string): LookupLink[] {
  const who = name?.trim()
  if (!who) return []
  const both = `${who} ${league.trim()}`.trim()
  return [
    { label: 'LinkedIn', href: `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(both)}` },
    { label: 'Google', href: `https://www.google.com/search?q=${encodeURIComponent(`"${who}" ${league.trim()}`.trim())}` },
    { label: 'Facebook', href: `https://www.facebook.com/search/top/?q=${encodeURIComponent(both)}` },
  ]
}

/** The State column: "Trial, 5 days left", "Locked 12 days", "Paid (now a tenant)", "Deleted", "Kept". */
export function stateLabel(l: Pick<SignupLifecycle, 'state' | 'trial_days_left' | 'locked_days'>): string {
  switch (l.state) {
    case 'trial':
      return l.trial_days_left === 0 ? 'Trial ends today' : `Trial, ${l.trial_days_left} ${l.trial_days_left === 1 ? 'day' : 'days'} left`
    case 'locked':
      return l.locked_days === null ? 'Locked' : `Locked ${l.locked_days} ${l.locked_days === 1 ? 'day' : 'days'}`
    case 'paid':
      return 'Paid (now a tenant)'
    case 'deleted':
      return 'Deleted'
    case 'kept':
      return 'Kept'
    default:
      return 'Not on a trial'
  }
}

export const STATE_TONE: Record<SignupStateKey, BadgeTone> = { trial: 'accent', locked: 'warn', paid: 'good', deleted: 'neutral', kept: 'outline', other: 'neutral' }

export const STATE_FILTER_LABELS: Record<SignupStateKey, string> = { trial: 'On trial', locked: 'Locked', paid: 'Paid', deleted: 'Deleted', kept: 'Kept', other: 'Not on a trial' }

/**
 * The deletion line: "Would delete on 4 Dec" while the cleanup is off,
 * "Deletes on 4 Dec" when it's on, "Deleted on 4 Dec", "Needs a look" when money
 * moved through the league, or null.
 */
export function deletionLabel(l: SignupLifecycle, format: (iso: string) => string): string | null {
  if (l.state === 'deleted') return l.league_deleted_at ? `Deleted on ${format(l.league_deleted_at)}` : 'Deleted'
  if (l.needs_look) return 'Needs a look'
  if (!l.deletion) return null
  return `${l.deletion.mode === 'would' ? 'Would delete on' : 'Deletes on'} ${format(l.deletion.on)}`
}

/** A fresh id for one press of Send, so a double click is one email. */
export function newRequestId(): string {
  const c = globalThis.crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}
